import {
  Columns,
  Home,
  InfoCircle,
  Photo,
  Receipt,
  Search,
  ShoppingBag,
  Tag,
  Tags,
  Template,
  User
} from 'tabler-icons-react';
import {
  ECOM_WEBSTORE_PAGE_TYPE_ENUM,
  ECOM_WEBSTORE_SECTION_BOUND_TYPE,
  ECOM_WEBSTORE_SECTION_TYPE_ENUM
} from '../../../../config/constants';
import { ECOM_WEBSTORE_SECTIONS } from '../sections/wsSectionConfig';
import { sterilizeUrlHandle } from '../../../../helpers/format';
import { PRODUCT_CATEGORY_LIST } from '../../../../config/productCategories';

const ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM = {
  MAIN: 1,
  USER: 2,
  // PAGE: 3,
  NAVIGATION: 4
};

const ECOM_WEBSTORE_PAGE_SUB_CATEGORIES = {
  CUSTOMER_ACCOUNTS: {
    title: 'Customer accounts',
    icon: User,
    value: 1
  }
};

const isPageSelected = (resourceNavigation, resource, requireId = false) => {
  const last = resourceNavigation[resourceNavigation.length - 1];
  if (!last) return false;

  if (requireId) {
    return last.type === resource && !!last.id;
  }
  return last.type === resource;
};

const ECOM_WEBSTORE_PAGE_CONFIG = {
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.HOME]: {
    title: 'Home page',
    sort: 1,
    icon: Home,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.HOME,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/',
    canAssignMenuItem: true,
    isSelected: (pathname) =>
      pathname === '' || pathname === '/' || pathname.startsWith('/home'),
    requiredSections: [],
    defaultSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.FEATURED_COLLECTION
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ]
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.PRODUCT]: {
    title: 'Products',
    sort: 2,
    icon: Tag,
    canCreateTemplate: false,
    canAssignResource: true,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.PRODUCT,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/products/{id}',
    canAssignMenuItem: true,
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'products', true),
    isSelected: (pathname) => pathname.startsWith('/products/'),
    getResourceOptions: (data) =>
      data.ecomStoreProducts.map((d) => ({
        label: d.name,
        value: d.uuid,
        image: d.previewImage,
        showImage: true
      })),
    getResourceValue: (resourceId, ecomStoreTheme) =>
      ecomStoreTheme.ecomStore.ecomStoreProducts.find(
        (d) => d.uuid === resourceId
      )?.urlHandle ?? '',
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.PRODUCT_INFORMATION
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.COLLECTION]: {
    title: 'Collections',
    sort: 4,
    icon: Tags,
    canCreateTemplate: false,
    canAssignResource: true,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.COLLECTION,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/collections/{id}',
    canAssignMenuItem: true,
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'products') ||
      isPageSelected(resourceNavigation, 'collections', true),
    isSelected: (pathname) =>
      pathname.startsWith('/collections/') || pathname === '/products',
    getResourceOptions: (data) =>
      data.ecomStoreCollections.map((d) => ({
        label: d.name,
        value: d.pkEcomStoreCollection.toString()
      })),
    getResourceValue: (resourceId, ecomStoreTheme) =>
      sterilizeUrlHandle(
        ecomStoreTheme.ecomStore.ecomStoreCollections.find(
          (d) => d.pkEcomStoreCollection.toString() === resourceId
        )?.name ?? ''
      ),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.RESOURCE_BANNER
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      },
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.PRODUCT_GRID],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.COLLECTION_LIST]: {
    title: 'Collections list',
    sort: 5,
    icon: Tags,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.COLLECTION_LIST,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/collections',
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'collections'),
    isSelected: (pathname) => pathname === '/collections',
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.COLLECTION_GRID
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.DESIGN]: {
    title: 'Designs',
    sort: 6,
    icon: Photo,
    canCreateTemplate: false,
    canAssignResource: true,
    canAssignMenuItem: true,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.DESIGN,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/designs/{id}',
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'designs', true),
    isSelected: (pathname) =>
      pathname.startsWith('/designs/') || pathname === '/products',
    getResourceOptions: (data) =>
      data.ecomStoreDesigns.map((d) => ({
        label: d.name,
        value: d.pkEcomVendorDesign.toString()
      })),
    getResourceValue: (resourceId, ecomStoreTheme) =>
      sterilizeUrlHandle(
        ecomStoreTheme.ecomStore.ecomStoreDesigns.find(
          (d) => d.pkEcomVendorDesign.toString() === resourceId
        )?.name ?? ''
      ),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.RESOURCE_BANNER
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      },
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.PRODUCT_GRID],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.DESIGN_LIST]: {
    title: 'Designs list',
    sort: 7,
    icon: Photo,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.DESIGN_LIST,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/designs',
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'designs'),
    isSelected: (pathname) => pathname === '/designs',
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.DESIGN_GRID],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CATEGORY]: {
    title: 'Categories',
    sort: 7,
    icon: Columns,
    canCreateTemplate: false,
    canAssignResource: true,
    canAssignMenuItem: true,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CATEGORY,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/categories/{id}',
    subPaths: ['/sub-categories/{id}'],
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'categories', true) ||
      isPageSelected(resourceNavigation, 'sub-categories', true),
    isSelected: (pathname) =>
      pathname.startsWith('/categories/') || pathname === '/products',
    getResourceOptions: (data) =>
      data.ecomCategories?.map((d) => ({
        label: d.name,
        value: d.pkEcomVendorDesign.toString()
      })) || [],
    getResourceValue: (resourceId) =>
      sterilizeUrlHandle(
        PRODUCT_CATEGORY_LIST.find((d) => d.value.toString() === resourceId)
          ?.name ?? ''
      ),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.RESOURCE_BANNER
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      },
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.PRODUCT_GRID],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CATEGORY_LIST]: {
    title: 'Categories list',
    sort: 8,
    icon: Columns,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CATEGORY_LIST,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.MAIN,
    path: '/categories',
    subPaths: ['/sub-categories/{id}'],
    isPageSelected: (pathname, resourceNavigation) =>
      isPageSelected(resourceNavigation, 'categories') ||
      isPageSelected(resourceNavigation, 'sub-categories'),
    isSelected: (pathname) => pathname === '/categories',
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.CATEGORY_GRID],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CART]: {
    title: 'Cart',
    sort: 9,
    icon: ShoppingBag,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CART,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    path: '/checkout/cart',
    isSelected: (pathname) => pathname === '/checkout/cart',
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.CHECKOUT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  // [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CHECKOUT]: {
  //   title: 'Checkout',
  //   sort: 7,
  //   icon: ShoppingCart,
  //   type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CHECKOUT,
  //   categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
  //   path: '/checkout',
  //   isSelected: (pathname) => pathname === '/checkout',
  //   requiredSections: [
  //     {
  //       config:
  //         ECOM_WEBSTORE_SECTIONS[
  //           ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
  //         ],
  //       location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
  //     }
  //   ],
  //   defaultSections: []
  // },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_LOGIN]: {
    title: 'Login',
    sort: 10,
    icon: User,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_LOGIN,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    subCategory: ECOM_WEBSTORE_PAGE_SUB_CATEGORIES.CUSTOMER_ACCOUNTS,
    path: '/account/login',
    isSelected: (pathname) =>
      pathname.startsWith('/account/login') ||
      pathname.startsWith('/account/forgot-password') ||
      pathname.startsWith('/account/reset-password') ||
      pathname.startsWith('/account/confirm-account'),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_REGISTER]: {
    title: 'Register',
    sort: 11,
    icon: User,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_REGISTER,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    subCategory: ECOM_WEBSTORE_PAGE_SUB_CATEGORIES.CUSTOMER_ACCOUNTS,
    path: '/account/register',
    isSelected: (pathname) => pathname.startsWith('/account/register'),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ACCOUNT]: {
    title: 'Account details',
    sort: 12,
    icon: User,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ACCOUNT,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    subCategory: ECOM_WEBSTORE_PAGE_SUB_CATEGORIES.CUSTOMER_ACCOUNTS,
    path: '/account/details',
    isSelected: (pathname) => pathname.startsWith('/account/details'),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ORDERS]: {
    title: 'Order history',
    sort: 13,
    icon: Receipt,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ORDERS,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    subCategory: ECOM_WEBSTORE_PAGE_SUB_CATEGORIES.CUSTOMER_ACCOUNTS,
    path: '/account/orders',
    isSelected: (pathname) =>
      pathname.startsWith('/account/orders') &&
      !pathname.startsWith('/account/orders/details'),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ORDER_DETAILS]: {
    title: 'Order details',
    sort: 14,
    icon: Receipt,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.CUSTOMER_ORDER_DETAILS,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.USER,
    subCategory: ECOM_WEBSTORE_PAGE_SUB_CATEGORIES.CUSTOMER_ACCOUNTS,
    path: '/account/orders/details',
    isSelected: (pathname) => pathname.startsWith('/account/orders/details'),
    requiredSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[
            ECOM_WEBSTORE_SECTION_TYPE_ENUM.ACCOUNT_CONTENT
          ],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  // [ECOM_WEBSTORE_PAGE_TYPE_ENUM.PAGE]: {
  //   title: 'Pages',
  //   sort: 12,
  //   icon: File,
  //   canCreateTemplate: true,
  //   type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.PAGE,
  //   categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.PAGE,
  //   path: '/pages/{id}',
  //   canAssignMenuItem: true,
  //   isSelected: (pathname) => pathname.startsWith('/pages/'),
  //   requiredSections: [],
  //   defaultSections: []
  // },
  // [ECOM_WEBSTORE_PAGE_TYPE_ENUM.BLOG]: {
  //   title: 'Blog',
  //   sort: 13,
  //   icon: File,
  //   canCreateTemplate: true,
  //   type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.BLOG,
  //   categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.PAGE,
  //   path: '/blogs/{id}',
  //   canAssignMenuItem: true,
  //   isSelected: (pathname) => pathname.startsWith('/blog/'),
  //   requiredSections: [],
  //   defaultSections: []
  // },
  // [ECOM_WEBSTORE_PAGE_TYPE_ENUM.BLOG_POST]: {
  //   title: 'Blog post',
  //   sort: 14,
  //   icon: File,
  //   canCreateTemplate: true,
  //   type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.BLOG_POST,
  //   categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.PAGE,
  //   path: '/blogs/{id}',
  //   canAssignMenuItem: true,
  //   isSelected: (pathname) => pathname.startsWith('/blog/'),
  //   requiredSections: [],
  //   defaultSections: []
  // },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.SEARCH]: {
    title: 'Search',
    sort: 15,
    icon: Search,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.SEARCH,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.NAVIGATION,
    path: '/search',
    canAssignMenuItem: true,
    isSelected: (pathname) => pathname === '/search',
    requiredSections: [],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.EXPLORE]: {
    title: 'Explore',
    sort: 16,
    icon: Search,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.EXPLORE,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.NAVIGATION,
    path: '/explore',
    canAssignMenuItem: true,
    isSelected: (pathname) => pathname === '/explore',
    requiredSections: [],
    defaultSections: [
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.CATEGORY_LIST],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      },
      {
        config:
          ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.DESIGN_LIST],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ]
  },
  // [ECOM_WEBSTORE_PAGE_TYPE_ENUM.PASSWORD]: {
  //   title: 'Password',
  //   sort: 16,
  //   icon: Lock,
  //   type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.PASSWORD,
  //   categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.NAVIGATION,
  //   path: '/password',
  //   isSelected: (pathname) => pathname === '/password',
  //   requiredSections: [],
  //   defaultSections: []
  // },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.ABOUT]: {
    title: 'About',
    sort: 17,
    hidden: true,
    icon: InfoCircle,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.ABOUT,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.NAVIGATION,
    path: '/about',
    isSelected: (pathname) =>
      pathname === '/about' || pathname.startsWith('/about/'),
    requiredSections: [
      {
        config: ECOM_WEBSTORE_SECTIONS[ECOM_WEBSTORE_SECTION_TYPE_ENUM.PAGE],
        location: ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY
      }
    ],
    defaultSections: []
  },
  [ECOM_WEBSTORE_PAGE_TYPE_ENUM.NOT_FOUND]: {
    title: '404 page (Not found)',
    sort: 18,
    icon: Template,
    type: ECOM_WEBSTORE_PAGE_TYPE_ENUM.NOT_FOUND,
    categoryType: ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM.NAVIGATION,
    path: '/404',
    isSelected: (pathname) => pathname === '/404',
    requiredSections: [],
    defaultSections: []
  }
};

const ECOM_WEBSTORE_PAGE_CONFIG_LIST = Object.keys(ECOM_WEBSTORE_PAGE_CONFIG)
  .reduce((r, c) => [...r, ECOM_WEBSTORE_PAGE_CONFIG[c]], [])
  .sort((a, b) => a.sort - b.sort);

export {
  ECOM_WEBSTORE_PAGE_CATEGORY_TYPE_ENUM,
  ECOM_WEBSTORE_PAGE_SUB_CATEGORIES,
  ECOM_WEBSTORE_PAGE_CONFIG,
  ECOM_WEBSTORE_PAGE_CONFIG_LIST
};
